import { CSSProperties, RefObject, useEffect, useRef, useState } from 'react';
import {
  resolveAutoSnapTargetScrollTop,
  shouldCompleteStickyTransition,
  TransitionMetrics,
} from './stickyRecipeHeader.helpers';
import {
  animateStickyHeaderScroll,
  canSettleStickyHeader,
  clearStickySettleTimeout,
  createScrollController,
  createStickyHeaderUpdater,
  resetStickyHeaderState,
  scheduleStickySettleTimeout,
  StickyHeaderStateSetters,
  stopStickyAutoSnap,
} from './stickyRecipeHeader.runtime';

interface UseStickyRecipeHeaderOptions {
  imageWrapperRef: RefObject<HTMLDivElement | null>;
  tapedPhotoEffectRef: RefObject<HTMLDivElement | null>;
  stickyImageButtonRef: RefObject<HTMLButtonElement | null>;
  disabled: boolean;
  recipeId?: string;
}

interface UseStickyRecipeHeaderResult {
  isStickyRecipeHeaderVisible: boolean;
  stickyHeaderProgress: number;
  sharedPhotoStyle: CSSProperties | null;
  isSharedPhotoVisible: boolean;
}

export const useStickyRecipeHeader = ({
  imageWrapperRef,
  tapedPhotoEffectRef,
  stickyImageButtonRef,
  disabled,
  recipeId,
}: UseStickyRecipeHeaderOptions): UseStickyRecipeHeaderResult => {
  const [isStickyRecipeHeaderVisible, setIsStickyRecipeHeaderVisible] = useState(false);
  const [stickyHeaderProgress, setStickyHeaderProgress] = useState(0);
  const [sharedPhotoStyle, setSharedPhotoStyle] = useState<CSSProperties | null>(null);
  const [isSharedPhotoVisible, setIsSharedPhotoVisible] = useState(false);

  const settleTimeoutRef = useRef<number | null>(null);
  const autoSnapFrameRef = useRef<number | null>(null);
  const autoSnapActiveRef = useRef(false);
  const isTouchScrollActiveRef = useRef(false);
  const transitionMetricsRef = useRef<TransitionMetrics | null>(null);
  const lastScrollTopRef = useRef(0);
  const lastScrollDirectionRef = useRef<1 | -1 | 0>(0);

  useEffect(() => {
    if (disabled) {
      resetStickyHeaderState({
        setIsStickyRecipeHeaderVisible,
        setStickyHeaderProgress,
        setIsSharedPhotoVisible,
        setSharedPhotoStyle,
      }, transitionMetricsRef);
      return;
    }

    const scrollContainer = document.getElementById('main-content');
    const transitionStartOffset = 88;
    const snapCompletionOffset = 72;
    const scrollController = createScrollController(scrollContainer);
    let frameId: number | null = null;

    const clearSettleTimeout = () => clearStickySettleTimeout(settleTimeoutRef);
    const stopAutoSnap = () => stopStickyAutoSnap({ autoSnapFrameRef, autoSnapActiveRef });

    const setters: StickyHeaderStateSetters = {
      setIsStickyRecipeHeaderVisible,
      setStickyHeaderProgress,
      setIsSharedPhotoVisible,
      setSharedPhotoStyle,
    };
    const updateStickyHeaderVisibility = createStickyHeaderUpdater({
      imageWrapperRef,
      tapedPhotoEffectRef,
      stickyImageButtonRef,
      scrollContainer,
      transitionStartOffset,
      snapCompletionOffset,
      setters,
      transitionMetricsRef,
      scrollController,
    });

    const settleScrollIfNeeded = () => {
      const metrics = canSettleStickyHeader({
        autoSnapActiveRef,
        isTouchScrollActiveRef,
        transitionMetricsRef,
      });
      if (!metrics) {
        return;
      }

      animateStickyHeaderScroll({
        targetScrollTop: resolveAutoSnapTargetScrollTop(metrics, lastScrollDirectionRef.current),
        autoSnapFrameRef,
        autoSnapActiveRef,
        stopAutoSnap,
        getCurrentScrollTop: scrollController.getCurrentScrollTop,
        setCurrentScrollTop: scrollController.setCurrentScrollTop,
        updateStickyHeaderVisibility,
      });
    };

    const scheduleUpdate = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updateStickyHeaderVisibility();
      });
    };

    const handleScroll = () => {
      const currentScrollTop = scrollController.getCurrentScrollTop();
      const previousScrollTop = lastScrollTopRef.current;
      const delta = currentScrollTop - previousScrollTop;

      if (currentScrollTop > lastScrollTopRef.current) {
        lastScrollDirectionRef.current = 1;
      } else if (currentScrollTop < lastScrollTopRef.current) {
        lastScrollDirectionRef.current = -1;
      }
      lastScrollTopRef.current = currentScrollTop;

      scheduleUpdate();

      if (autoSnapActiveRef.current) {
        return;
      }

      const metrics = transitionMetricsRef.current;
      const shouldCompleteOngoingTransition = shouldCompleteStickyTransition(metrics, delta);

      scheduleStickySettleTimeout({
        settleTimeoutRef,
        clearSettleTimeout,
        settleScrollIfNeeded,
        shouldCompleteOngoingTransition,
      });
    };

    const handleUserInterrupt = () => {
      if (!autoSnapActiveRef.current) {
        return;
      }

      stopAutoSnap();
      clearSettleTimeout();
    };

    const handleTouchStart = () => {
      isTouchScrollActiveRef.current = true;
      clearSettleTimeout();
      handleUserInterrupt();
    };

    const handleTouchEnd = () => {
      isTouchScrollActiveRef.current = false;
      clearSettleTimeout();

      const metrics = transitionMetricsRef.current;
      if (!metrics || metrics.progress <= 0 || metrics.progress >= 1) {
        return;
      }

      settleTimeoutRef.current = window.setTimeout(settleScrollIfNeeded, 60);
    };

    updateStickyHeaderVisibility();
    lastScrollTopRef.current = scrollController.getCurrentScrollTop();
    lastScrollDirectionRef.current = 0;
    scrollContainer?.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('wheel', handleUserInterrupt, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    window.addEventListener('keydown', handleUserInterrupt);

    return () => {
      clearSettleTimeout();
      stopAutoSnap();
      isTouchScrollActiveRef.current = false;
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      scrollContainer?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('wheel', handleUserInterrupt);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
      window.removeEventListener('keydown', handleUserInterrupt);
    };
  }, [disabled, imageWrapperRef, recipeId, stickyImageButtonRef, tapedPhotoEffectRef]);

  return {
    isStickyRecipeHeaderVisible,
    stickyHeaderProgress,
    sharedPhotoStyle,
    isSharedPhotoVisible,
  };
};

export default useStickyRecipeHeader;
